import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { selectCxbSelectedTenant } from '../../../../app.selectors';
import { filter, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { RootAppState } from '../../../../app.reducer';
import { selectCxbPage } from '../../../page-edit-screen.selectors';
import { AppState } from '../../../page-edit-screen.reducer';
import { EditorialPageVersionItem, PageVersionItem } from '../../../../domain/page-version';

@Injectable({
  providedIn: 'root',
})
export class PageEditScreenValidationService {
  constructor(private readonly store: Store<RootAppState>, private readonly editScreenStore: Store<AppState>) {}

  private readonly pageInformationForValidation$ = combineLatest([
    this.store.select(selectCxbSelectedTenant),
    this.editScreenStore.select(selectCxbPage),
  ]).pipe(
    filter(([selectedTenant, page]) => page?.type === 'Content' && !!page?.pageItems && !!selectedTenant?.name),
    map(([{ name }, { pageItems }]) => {
      return {
        isOSNPlusTenant: name === 'OSNPLUS',
        isOnDemandTenant: name === 'ONDEMAND',
        pageItems,
      };
    })
  );

  public isOnDemandPageValidForPublishing$: Observable<boolean> = this.pageInformationForValidation$.pipe(
    map(({ isOnDemandTenant, pageItems }) => {
      if (isOnDemandTenant) {
        return pageItems.some(({ trayType, visible }) => trayType === 'HeroSpotlight' && visible);
      }
      return true;
    })
  );

  public isOSNPlusPageValidForPublishing$: Observable<boolean> = this.pageInformationForValidation$.pipe(
    map(({ isOSNPlusTenant, pageItems }) => {
      if (isOSNPlusTenant) {
        return pageItems.some(({ trayType, visible }) => trayType === 'HeroPageItem' && visible);
      }
      return true;
    })
  );

  public isMarketingEmailBlockingPageItemValidForPublishing$: Observable<boolean> = this.pageInformationForValidation$.pipe(
    map(({ isOSNPlusTenant, pageItems }) => {
      const allowedTypesBeforeMarketingEmailBlocker = [
        'PortraitPageItem',
        'TopTenPageItem',
        'ExpandablePageItem',
        'CollectionPageItem',
        'SeasonPageItem',
      ];
      if (isOSNPlusTenant) {
        return !!pageItems.every(
          ({ trayType }, index: number) =>
            trayType !== 'MarketingEmailBlockerPageItem' || allowedTypesBeforeMarketingEmailBlocker.includes(pageItems[index - 1]?.trayType)
        );
      }
      return true;
    })
  );

  public isMarketingEmailBlockingPageUnique$: Observable<boolean> = this.pageInformationForValidation$.pipe(
    map(({ isOSNPlusTenant, pageItems }) => {
      if (isOSNPlusTenant) {
        return pageItems.filter(({ trayType }) => trayType === 'MarketingEmailBlockerPageItem').length <= 1;
      }
      return true;
    })
  );

  public isRecommendationEditorialPageUseCaseExists$: Observable<boolean> = this.pageInformationForValidation$.pipe(
    map(({ isOSNPlusTenant, pageItems }) => {
      if (isOSNPlusTenant) {
        return !pageItems.some(
          (pageItem: PageVersionItem) =>
            pageItem.trayType === 'RecommendationEditorialPageItem' && (pageItem as EditorialPageVersionItem).message !== null
        );
      }
      return true;
    })
  );

  public isPageValidForPublishing$ = combineLatest([
    this.isOnDemandPageValidForPublishing$,
    this.isOSNPlusPageValidForPublishing$,
    this.isMarketingEmailBlockingPageUnique$,
    this.isMarketingEmailBlockingPageItemValidForPublishing$,
    this.isRecommendationEditorialPageUseCaseExists$,
  ]).pipe(
    map(
      ([
        isOnDemandPageValidForPublishing,
        isOSNPlusPageValidForPublishing,
        isMarketingEmailBlockingPageUnique,
        isMarketingEmailBlockingPageItemValidForPublishing,
        isRecommendationEditorialPageUseCaseExists,
      ]) => {
        return (
          isOnDemandPageValidForPublishing &&
          isOSNPlusPageValidForPublishing &&
          isMarketingEmailBlockingPageUnique &&
          isMarketingEmailBlockingPageItemValidForPublishing &&
          isRecommendationEditorialPageUseCaseExists
        );
      }
    )
  );
}
